import type { TimelineEntry, TimelineEventKind } from '../api/types'
import { DEFECT_PROFILE_TITLE } from './defectProfile'
import { formatLongDate, formatNumber } from './format'
import { isPremiumTimelineEvent } from './premium'

/**
 * The vehicle's life as one ordering, in Dutch.
 *
 * Everything the timeline shows is already somewhere else in the report. What it adds is the
 * ordering: the RDW publishes registrations, inspections, retrofits and recalls as four unrelated
 * lists, and what a used-car buyer wants to notice — a five-year gap with no inspection, a tow bar
 * fitted the month before the car changed hands, a recall announced two owners ago — is only
 * visible once they sit on one axis.
 *
 * **Oldest first**, unlike `inspection.history` and `recalls`. Those are lists of the most recent
 * thing; this is a life story and it starts at the beginning.
 */

export const TIMELINE_TITLE = 'De geschiedenis van deze auto'

export const TIMELINE_SUBTITLE = 'Toelating, tenaamstelling, keuringen, inbouw en terugroepacties op één tijdlijn'

/** How firmly one entry reads. `future` is the APK expiry that has not happened yet. */
export type TimelineTone = 'neutral' | 'warning' | 'negative' | 'future'

/** One event, ready to render. */
export interface TimelineRow {
  kind: TimelineEventKind
  /** The date, written out. */
  date: string
  title: string
  /** A second line: what the RDW called it. */
  detail?: string
  /**
   * The part of the second line that goes somewhere, where there is one.
   *
   * The timeline is a summary of four cards further up the page, and the number on an entry is the
   * hook back to the card it was summarised from: "1 bevinding" belongs to the inspection history,
   * a recall's reference to the recall itself. Carrying the target here rather than in the component
   * keeps the anchor beside the wording that describes it.
   */
  link?: { text: string; href: string }
  /**
   * A link forwards rather than backwards, on the one entry that has not happened yet.
   *
   * Every other link here points at the card an entry was summarised from. This one points at
   * "Wat gaat er bij dit model vaak mis?", which is about other vehicles and is the only
   * forward-looking content in the report: the APK expiry is where a reader is thinking about the
   * next keuring, and that card is thirteen thousand pixels below them.
   *
   * It rides on the title line rather than taking a line of its own because the defect profile
   * arrives from its own endpoint seconds after the report, and a line appearing then would shove
   * the four cards below the timeline down under someone who has started reading.
   */
  lookahead?: { text: string; href: string }
  tone: TimelineTone
  /**
   * Whether this entry is paid content. The card is free; the keuringen, recalls and retrofits
   * inside it are not — see `isPremiumTimelineEvent`, which owns that decision.
   */
  premium: boolean
}

const TITLES: Record<TimelineEventKind, string> = {
  FirstAdmission: 'Eerste toelating',
  FirstAdmissionInNetherlands: 'Eerste toelating in Nederland',
  RegisteredToCurrentKeeper: 'Tenaamstelling op de huidige eigenaar',
  Inspection: 'Keuring',
  EquipmentFitted: 'Object ingebouwd',
  EquipmentRemoved: 'Object verwijderd',
  RecallAnnounced: 'Terugroepactie aangekondigd',
  ApkExpiry: 'APK verloopt',
}

/** One or more entries that share a date and belong on one line. */
interface Group {
  entry: TimelineEntry
  /** How many inspections were folded into this line. One for everything that is not an inspection. */
  inspections: number
  /** Their findings added up. */
  defects: number
  /** What the RDW called each of them, without repeats. */
  descriptions: string[]
}

export interface TimelineOptions {
  /**
   * Whether "Wat gaat er bij dit model vaak mis?" rendered on this report, which decides whether the
   * APK expiry gets its forward link.
   *
   * The caller has to answer this because the timeline cannot: that card comes from its own
   * endpoint, is gated on `hasDefectProfile`, and carries no figures more often than not. An anchor
   * to a card that is not there scrolls nowhere and a browser gives no sign that anything was meant
   * to happen.
   */
  hasDefectProfile?: boolean
}

/**
 * Turns the API's entries into rows.
 *
 * `isFuture` comes from the API rather than from a comparison against the browser's clock, and that
 * is not fussiness: a report is cached for twelve hours, so a client deciding for itself would
 * eventually disagree with the rest of the document about whether the APK has run out.
 */
export function summariseTimeline(
  entries: TimelineEntry[] | undefined,
  options: TimelineOptions = {},
): TimelineRow[] {
  return group(entries ?? []).map((item) => ({
    kind: item.entry.kind,
    date: formatLongDate(item.entry.date) ?? item.entry.date,
    title: title(item),
    detail: detail(item),
    link: link(item),
    lookahead: lookahead(item, options),
    tone: tone(item),
    premium: isPremiumTimelineEvent(item.entry.kind),
  }))
}

/**
 * Folds inspections that share a date onto one line.
 *
 * A vehicle is routinely inspected several times in a day — a failed APK, the repairs, the
 * re-inspection — and the RDW reports each of them, which is right and is why the report's own
 * inspection card lists them all. On a timeline it is ruinous: one real Peugeot Partner produces
 * nineteen consecutive lines reading "13 juni 2024 — keuring", and an axis whose whole purpose is
 * showing runs and gaps has neither left.
 *
 * Nothing is hidden by this. The count and the findings are added up and stated on the line, the
 * separate inspections are all in "Onderhoud en APK" above, and "19 keuringen op één dag" is itself
 * the sort of thing this section exists to make visible.
 */
function group(entries: TimelineEntry[]): Group[] {
  const groups: Group[] = []

  for (const entry of entries) {
    const previous = groups.at(-1)

    if (
      previous &&
      entry.kind === 'Inspection' &&
      previous.entry.kind === 'Inspection' &&
      previous.entry.date === entry.date
    ) {
      previous.inspections += 1
      previous.defects += entry.defectCount ?? 0

      if (entry.description && !previous.descriptions.includes(entry.description)) {
        previous.descriptions.push(entry.description)
      }

      continue
    }

    groups.push({
      entry,
      inspections: 1,
      defects: entry.defectCount ?? 0,
      descriptions: entry.description ? [entry.description] : [],
    })
  }

  return groups
}

function title({ entry, inspections }: Group): string {
  if (entry.kind === 'ApkExpiry') {
    return entry.isFuture ? 'APK verloopt' : 'APK verlopen'
  }

  if (entry.kind === 'Inspection' && inspections > 1) {
    return `${formatNumber(inspections)} keuringen op één dag`
  }

  return TITLES[entry.kind] ?? entry.kind
}

/**
 * The second line.
 *
 * An inspection always states its findings, including "geen bevindingen": the whole point of the
 * timeline is the gaps and the runs, and a clean inspection that said nothing would read as an
 * inspection whose result was not published.
 */
function detail({ entry, descriptions }: Group): string | undefined {
  const parts: string[] = [...descriptions]

  if (entry.kind === 'FirstAdmissionInNetherlands') {
    parts.push('geïmporteerd')
  }

  return parts.length > 0 ? parts.join(', ') : undefined
}

/**
 * The clickable half of the second line, pointing at the card this entry was summarised from.
 *
 * The anchors are the `SectionSpec` ids in `ReportView`. Renaming one there without changing it here
 * leaves a link that scrolls nowhere, which is silent: the browser simply does not move.
 */
function link({ entry, defects }: Group): TimelineRow['link'] {
  if (entry.kind === 'Inspection') {
    return {
      text:
        defects === 0
          ? 'geen bevindingen'
          : `${formatNumber(defects)} ${defects === 1 ? 'bevinding' : 'bevindingen'}`,
      href: '#apk',
    }
  }

  if (entry.kind === 'RecallAnnounced' && entry.reference) {
    return { text: entry.reference, href: '#terugroepacties' }
  }

  if (entry.kind === 'EquipmentFitted' || entry.kind === 'EquipmentRemoved') {
    return { text: 'bekijk object', href: '#objecten' }
  }

  return undefined
}

/**
 * The forward link, on the APK expiry and nowhere else.
 *
 * Two guards, and both are about not making a claim the card cannot support.
 *
 * `isFuture` is one of them: on an expiry that has already passed the entry reads "APK verlopen",
 * and a link to what this model is commonly failed on beside it reads as a finding against the
 * vehicle in front of the reader. Nothing in that card was observed on this car.
 *
 * The other is `hasDefectProfile`, because that card is absent from most reports.
 */
function lookahead({ entry }: Group, options: TimelineOptions): TimelineRow['lookahead'] {
  if (entry.kind !== 'ApkExpiry' || !entry.isFuture || !options.hasDefectProfile) {
    return undefined
  }

  // The card's own heading is the link text, so the two cannot drift apart and a reader arrives at
  // the words they clicked. The framing is carried by where it sits rather than by the wording: on
  // the one dated thing in the report that has not happened yet, a question about this model needs
  // no sentence introducing it.
  return { text: DEFECT_PROFILE_TITLE, href: '#modelgebreken' }
}

function tone({ entry, defects }: Group): TimelineTone {
  if (entry.kind === 'ApkExpiry') {
    return entry.isFuture ? 'future' : 'negative'
  }

  if (entry.kind === 'RecallAnnounced') {
    return 'warning'
  }

  if (entry.kind === 'Inspection' && defects > 0) {
    return 'warning'
  }

  return 'neutral'
}
